import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ExternalLink, Folder } from 'lucide-react';
import { PRODUCT_TYPES, type Investor } from '@/types/investor';
import { formatPhoneNumber, formatUrl, telHref } from '@/utils/investor/validation';
import { ChannelCard } from '../../profile/ChannelCard';
import type { RelationshipSectionsProps } from './types';

export function InvestorRelationshipSections({
  investor,
  draft,
  editingSection,
  hasWholesale,
  hasCorrespondent,
  updateDraft,
  sectionActions,
  toggleDraftProduct,
  websiteLabel
}: RelationshipSectionsProps) {
  const formatDisplayDate = (dateString?: string): string => {
    if (!dateString) {
      return '';
    }
    const date = new Date(`${dateString}${dateString.length === 10 ? 'T00:00:00' : ''}`);
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusPill = (status: Investor['status'] | Investor['correspondentStatus']) => {
    const kind = status === 'Approved' ? 'ok' : status === 'Pending' ? 'warn' : 'neutral';
    return (
      <span className={`lw-status-pill ${kind}`}>
        <span className={`lw-dot ${kind}`} />
        {status}
      </span>
    );
  };

  const channelLabel = (channel: 'w' | 'c', label: string) => (
    <span className="lw-channel-row-label">
      <span className={`lw-channel-mark ${channel}`}>{channel.toUpperCase()}</span>
      {label}
    </span>
  );

  const renderProductEditor = (channel: 'w' | 'c', label: string, selectedProducts: string[]) => {
    const selected = new Set(selectedProducts);
    return (
      <div className={`lw-product-editor-channel ${channel}`}>
        <div className="lw-product-editor-head">
          {channelLabel(channel, label)}
          <span className="lw-product-editor-count">{selected.size} selected</span>
        </div>
        <div className="lw-product-checkbox-grid">
          {PRODUCT_TYPES.map((product) => (
            <label key={`${channel}-${product}`} className="lw-product-checkbox">
              <input
                type="checkbox"
                checked={selected.has(product)}
                onChange={() => toggleDraftProduct(channel, product)}
              />
              <span>{product}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {(hasWholesale || hasCorrespondent) ? (
        <div
          className="lw-channel-split"
          style={{
            gridTemplateColumns: hasWholesale && hasCorrespondent ? undefined : '1fr',
            maxWidth: hasWholesale && hasCorrespondent ? 'none' : 520,
            marginTop: 0
          }}
        >
          {hasWholesale && <ChannelCard channel="wholesale" investor={investor} />}
          {hasCorrespondent && <ChannelCard channel="correspondent" investor={investor} />}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No channels enabled for this investor.</p>
      )}

      <Card className="lw-section-card">
        <CardHeader className="lw-section-head flex-row space-y-0">
          <h4 className="lw-section-title">Investor Status</h4>
          {sectionActions('status', ['canDoWholesale', 'canGoCorrespondent', 'status', 'approvedAsOfDate', 'correspondentStatus', 'correspondentApprovedAsOfDate'])}
        </CardHeader>
        <CardContent className="lw-section-content">
          {editingSection === 'status' ? (
            <div className="space-y-4 lw-detail-stack">
              <div className="lw-aif-channel-grid">
                <button className={`lw-aif-channel ${draft.canDoWholesale ? 'active' : ''}`} onClick={() => updateDraft('canDoWholesale', !(draft.canDoWholesale ?? false))} type="button">
                  <span className="lw-channel-card-mark">W</span>
                  Wholesale
                </button>
                <button className={`lw-aif-channel ${draft.canGoCorrespondent ? 'active' : ''}`} onClick={() => updateDraft('canGoCorrespondent', !(draft.canGoCorrespondent ?? false))} type="button">
                  <span className="lw-channel-card-mark c">C</span>
                  Correspondent
                </button>
              </div>
              {draft.canDoWholesale && (
                <div className="lw-edit-grid">
                  <label className="lw-field">
                    <span className="lw-label">Wholesale status</span>
                    <select className="lw-perpage" value={draft.status ?? 'Pending'} onChange={(e) => updateDraft('status', e.target.value as Investor['status'])}>
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </label>
                  <label className="lw-field">
                    <span className="lw-label">Wholesale approved date</span>
                    <Input className="lw-input" type="date" value={draft.approvedAsOfDate ?? ''} onChange={(e) => updateDraft('approvedAsOfDate', e.target.value || undefined)} />
                  </label>
                </div>
              )}
              {draft.canGoCorrespondent && (
                <div className="lw-edit-grid">
                  <label className="lw-field">
                    <span className="lw-label">Correspondent status</span>
                    <select className="lw-perpage" value={draft.correspondentStatus ?? 'Pending'} onChange={(e) => updateDraft('correspondentStatus', e.target.value as Investor['correspondentStatus'])}>
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </label>
                  <label className="lw-field">
                    <span className="lw-label">Correspondent approved date</span>
                    <Input className="lw-input" type="date" value={draft.correspondentApprovedAsOfDate ?? ''} onChange={(e) => updateDraft('correspondentApprovedAsOfDate', e.target.value || undefined)} />
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="lw-channel-row-list">
              {hasWholesale && (
                <div className="lw-channel-row">
                  <div className="lw-channel-row-key">{channelLabel('w', 'Wholesale')}</div>
                  <div className="lw-channel-row-value">
                    {statusPill(investor.status)}
                    {investor.approvedAsOfDate && (
                      <span className="lw-channel-row-note">since {formatDisplayDate(investor.approvedAsOfDate)}</span>
                    )}
                  </div>
                </div>
              )}
              {hasCorrespondent && (
                <div className="lw-channel-row">
                  <div className="lw-channel-row-key">{channelLabel('c', 'Correspondent')}</div>
                  <div className="lw-channel-row-value">
                    {statusPill(investor.correspondentStatus)}
                    {investor.correspondentApprovedAsOfDate && (
                      <span className="lw-channel-row-note">since {formatDisplayDate(investor.correspondentApprovedAsOfDate)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lw-section-card">
        <CardHeader className="lw-section-head flex-row space-y-0">
          <h4 className="lw-section-title">Website</h4>
          {sectionActions('website', ['website'])}
        </CardHeader>
        <CardContent className="lw-section-content">
          {editingSection === 'website' ? (
            <label className="lw-field">
              <span className="lw-label">Company website</span>
              <Input
                className="lw-input"
                value={draft.website ?? ''}
                onChange={(event) => updateDraft('website', event.target.value)}
                onBlur={(event) => updateDraft('website', formatUrl(event.target.value))}
                placeholder="https://example.com"
              />
            </label>
          ) : (
            <div className="lw-kv">
              <div className="lw-kv-key">Company website</div>
              <div className="lw-kv-value">
                {investor.website ? (
                  <a
                    href={formatUrl(investor.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="lw-link"
                  >
                    {websiteLabel} <ExternalLink size={11} />
                  </a>
                ) : (
                  <span className="lw-kv-value muted">Not set</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lw-section-card">
        <CardHeader className="lw-section-head flex-row space-y-0">
          <h4 className="lw-section-title"><Folder size={14} /> Products by Channel</h4>
          {sectionActions('products', ['wholesaleProducts', 'correspondentProducts'])}
        </CardHeader>
        <CardContent className="lw-section-content">
          {editingSection === 'products' ? (
            <div className="lw-product-editor">
              {hasWholesale && renderProductEditor('w', 'Wholesale', draft.wholesaleProducts ?? [])}
              {hasCorrespondent && renderProductEditor('c', 'Correspondent', draft.correspondentProducts ?? [])}
            </div>
          ) : (
            <div className="lw-channel-row-list">
              {hasWholesale && (
                <div className="lw-channel-row">
                  <div className="lw-channel-row-key">{channelLabel('w', 'Wholesale')}</div>
                  <div className="lw-channel-row-value wrap">
                    {investor.wholesaleProducts?.length ? (
                      investor.wholesaleProducts.map((product) => <span key={product} className="lw-chip">{product}</span>)
                    ) : (
                      <span className="lw-kv-value muted">None selected</span>
                    )}
                  </div>
                </div>
              )}
              {hasCorrespondent && (
                <div className="lw-channel-row">
                  <div className="lw-channel-row-key">{channelLabel('c', 'Correspondent')}</div>
                  <div className="lw-channel-row-value wrap">
                    {investor.correspondentProducts?.length ? (
                      investor.correspondentProducts.map((product) => <span key={product} className="lw-chip">{product}</span>)
                    ) : (
                      <span className="lw-kv-value muted">None selected</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lw-section-card">
        <CardHeader className="lw-section-head flex-row space-y-0">
          <h4 className="lw-section-title">Account Executives</h4>
          {sectionActions('contacts', ['wholesaleAeName', 'wholesaleAePhone', 'wholesaleAeEmail', 'correspondentAeName', 'correspondentAePhone', 'correspondentAeEmail', 'aeName', 'aePhone', 'aeEmail'])}
        </CardHeader>
        <CardContent className="lw-section-content">
          {editingSection === 'contacts' ? (
            <div className="lw-edit-grid">
              {hasWholesale && (
                <>
                  <label className="lw-field"><span className="lw-label">Wholesale AE</span><Input className="lw-input" value={draft.wholesaleAeName ?? ''} onChange={(e) => updateDraft('wholesaleAeName', e.target.value)} /></label>
                  <label className="lw-field"><span className="lw-label">Wholesale AE phone</span><Input className="lw-input" value={draft.wholesaleAePhone ?? ''} onChange={(e) => updateDraft('wholesaleAePhone', formatPhoneNumber(e.target.value))} /></label>
                  <label className="lw-field"><span className="lw-label">Wholesale AE email</span><Input className="lw-input" value={draft.wholesaleAeEmail ?? ''} onChange={(e) => updateDraft('wholesaleAeEmail', e.target.value)} /></label>
                </>
              )}
              {hasCorrespondent && (
                <>
                  <label className="lw-field"><span className="lw-label">Correspondent AE</span><Input className="lw-input" value={draft.correspondentAeName ?? ''} onChange={(e) => updateDraft('correspondentAeName', e.target.value)} /></label>
                  <label className="lw-field"><span className="lw-label">Correspondent AE phone</span><Input className="lw-input" value={draft.correspondentAePhone ?? ''} onChange={(e) => updateDraft('correspondentAePhone', formatPhoneNumber(e.target.value))} /></label>
                  <label className="lw-field"><span className="lw-label">Correspondent AE email</span><Input className="lw-input" value={draft.correspondentAeEmail ?? ''} onChange={(e) => updateDraft('correspondentAeEmail', e.target.value)} /></label>
                </>
              )}
            </div>
          ) : (
            <div className="lw-edit-grid" style={{ gridTemplateColumns: hasWholesale && hasCorrespondent ? '1fr 1fr' : '1fr' }}>
              {hasWholesale && (
                <div>
                  <div className="lw-label">Wholesale AE</div>
                  <div className="text-sm text-foreground">{investor.wholesaleAeName || 'Not set'}</div>
                  {investor.wholesaleAeEmail && (
                    <div className="text-xs"><a className="text-muted-foreground hover:underline" href={`mailto:${investor.wholesaleAeEmail}`}>{investor.wholesaleAeEmail}</a></div>
                  )}
                  {investor.wholesaleAePhone && telHref(investor.wholesaleAePhone) && (
                    <div className="text-xs"><a className="text-muted-foreground hover:underline" href={`tel:${telHref(investor.wholesaleAePhone)}`}>{formatPhoneNumber(investor.wholesaleAePhone)}</a></div>
                  )}
                </div>
              )}
              {hasCorrespondent && (
                <div>
                  <div className="lw-label">Correspondent AE</div>
                  <div className="text-sm text-foreground">{investor.correspondentAeName || 'Not set'}</div>
                  {investor.correspondentAeEmail && (
                    <div className="text-xs"><a className="text-muted-foreground hover:underline" href={`mailto:${investor.correspondentAeEmail}`}>{investor.correspondentAeEmail}</a></div>
                  )}
                  {investor.correspondentAePhone && telHref(investor.correspondentAePhone) && (
                    <div className="text-xs"><a className="text-muted-foreground hover:underline" href={`tel:${telHref(investor.correspondentAePhone)}`}>{formatPhoneNumber(investor.correspondentAePhone)}</a></div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
